import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container", "configField", "emptyState", "coverageSummary", "starterPrompts", "toolCheckbox", "guideUrl",
    "htmlFastModel", "htmlFullModel", "htmlFastStaticByDefault", "htmlFastFixedStyle",
    "htmlFullFixedStyle", "htmlFullEnableThinking", "htmlFullThinkingBudget", "htmlFullRetryWithoutThinking",
    "htmlRouterModel", "htmlRouterJsonMode", "webArtifactsEnabled"
  ]

  connect() {
    this.loadExistingConfig()
    this.toggleEmptyState()
  }

  loadExistingConfig() {
    try {
      const configJson = this.configFieldTarget.value
      if (!configJson) return

      const config = JSON.parse(configJson)

      if (this.hasCoverageSummaryTarget) {
        this.coverageSummaryTarget.value = config.coverage_summary || ""
      }

      if (this.hasStarterPromptsTarget) {
        const starterText = (config.starter_prompts || [])
          .filter((prompt) => prompt.title && prompt.prompt)
          .map((prompt) => `${prompt.title} | ${prompt.prompt}`)
          .join("\n")
        this.starterPromptsTarget.value = starterText
      }

      if (this.hasGuideUrlTarget) {
        this.guideUrlTarget.value = config.guide_url || ""
      }

      if (this.hasHtmlFastModelTarget) {
        this.htmlFastModelTarget.value = config.fast_model || "deepseek-ai/DeepSeek-V4-Flash"
      }

      if (this.hasHtmlRouterModelTarget) {
        this.htmlRouterModelTarget.value = config.router_model || ""
      }

      if (this.hasHtmlRouterJsonModeTarget) {
        this.htmlRouterJsonModeTarget.checked = config.router_json_mode !== false
      }

      if (this.hasHtmlFullModelTarget) {
        this.htmlFullModelTarget.value = config.model || "Pro/moonshotai/Kimi-K2.5"
      }

      if (this.hasHtmlFastStaticByDefaultTarget) {
        this.htmlFastStaticByDefaultTarget.checked = config.fast_static_by_default !== false
      }

      if (this.hasHtmlFastFixedStyleTarget) {
        this.htmlFastFixedStyleTarget.checked = config.fast_fixed_style !== false
      }

      if (this.hasHtmlFullFixedStyleTarget) {
        this.htmlFullFixedStyleTarget.checked = config.full_fixed_style !== false
      }

      if (this.hasHtmlFullEnableThinkingTarget) {
        this.htmlFullEnableThinkingTarget.checked = config.full_enable_thinking !== false
      }

      if (this.hasHtmlFullThinkingBudgetTarget) {
        this.htmlFullThinkingBudgetTarget.value = config.full_thinking_budget || ""
      }

      if (this.hasHtmlFullRetryWithoutThinkingTarget) {
        this.htmlFullRetryWithoutThinkingTarget.checked = config.full_retry_without_thinking !== false
      }

      if (this.hasWebArtifactsEnabledTarget) {
        this.webArtifactsEnabledTarget.checked = config.web_artifacts_enabled === true
      }

      if (config.inputs && Array.isArray(config.inputs)) {
        config.inputs.forEach((input, index) => this.addInputWithData(input, index > 0))
      }

      if (this.hasToolCheckboxTarget) {
        const tools = Array.isArray(config.tools) ? config.tools : null
        const hasDocumentSearchFlag = Object.prototype.hasOwnProperty.call(config, "document_search_enabled")

        this.toolCheckboxTargets.forEach((checkbox) => {
          if (checkbox.value === "document_search") {
            checkbox.checked = config.document_search_enabled !== false && (!tools || tools.includes("document_search") || !hasDocumentSearchFlag)
          } else {
            checkbox.checked = tools ? tools.includes(checkbox.value) : false
          }
        })
      }

      // Re-sync configField after all UI state (including tool checkboxes) is restored
      // This is necessary because addInputWithData() calls updateConfig() before
      // tool checkboxes are set, causing tools to be incorrectly serialized as []
      this.updateConfig()
    } catch (e) {
      console.error("Error loading existing config:", e)
    }
  }

  addInput(event) {
    if (event) event.preventDefault()
    this.addInputWithData({}, false)
  }

  addInputWithData(data = {}, collapsed = false) {
    const index = this.containerTarget.children.length
    const inputType = data.input_type || "text"
    const inputDiv = document.createElement("div")
    inputDiv.className = "border border-border rounded-lg bg-muted/30 overflow-hidden"
    inputDiv.dataset.index = index

    inputDiv.innerHTML = `
      <div class="flex justify-between items-center px-4 py-2 border-b border-border bg-muted/40">
        <button type="button" class="flex items-center gap-2 min-w-0 text-left" data-action="click->agent-inputs#toggleInput">
          <span class="input-index-badge flex items-center justify-center w-5 h-5 rounded bg-primary/10 text-primary text-[11px] font-bold">${index + 1}</span>
          <h4 class="text-sm font-medium text-foreground truncate input-heading">${data.name ? `${data.name}` : `Input #${index + 1}`}</h4>
          <span class="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground input-type-badge">${inputType}</span>
          <svg class="input-chevron w-3.5 h-3.5 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/></svg>
        </button>
        <button type="button" class="inline-flex items-center gap-1 text-xs text-destructive/70 hover:text-destructive transition-colors" data-action="click->agent-inputs#removeInput">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          Remove
        </button>
      </div>

      <div class="input-body p-4 grid grid-cols-6 gap-3 ${collapsed ? "hidden" : ""}">
        <div class="col-span-2">
          <label class="block text-xs font-medium text-foreground mb-1">Field Name</label>
          <input type="text" class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm input-name" placeholder="e.g., user_age" value="${data.name || ""}" data-action="input->agent-inputs#syncInputMeta change->agent-inputs#updateConfig">
        </div>

        <div class="col-span-2">
          <label class="block text-xs font-medium text-foreground mb-1">Label</label>
          <input type="text" class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm input-label" placeholder="e.g., Your Age" value="${data.label || ""}" data-action="change->agent-inputs#updateConfig">
        </div>

        <div class="col-span-1">
          <label class="block text-xs font-medium text-foreground mb-1">Type</label>
          <select class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm input-type" data-action="change->agent-inputs#handleTypeChange">
            <option value="text" ${inputType === "text" ? "selected" : ""}>Text</option>
            <option value="textarea" ${inputType === "textarea" ? "selected" : ""}>Textarea</option>
            <option value="select" ${inputType === "select" ? "selected" : ""}>Select</option>
            <option value="file" ${inputType === "file" ? "selected" : ""}>File</option>
          </select>
        </div>

        <div class="col-span-1 flex items-end gap-3 pb-0.5">
          <label class="inline-flex items-center gap-1.5 text-xs text-foreground cursor-pointer">
            <input type="checkbox" class="rounded border-input text-primary focus:ring-primary input-required" ${data.required ? "checked" : ""} data-action="change->agent-inputs#updateConfig">
            Required
          </label>
          <label class="inline-flex items-center gap-1.5 text-xs text-foreground cursor-pointer">
            <input type="checkbox" class="rounded border-input text-primary focus:ring-primary input-primary" ${data.primary ? "checked" : ""} data-action="change->agent-inputs#updateConfig">
            Primary
          </label>
        </div>

        <div class="col-span-6 input-placeholder-container" ${inputType === "select" || inputType === "file" ? "style=\"display:none\"" : ""}>
          <label class="block text-xs font-medium text-foreground mb-1">Placeholder</label>
          <input type="text" class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm input-placeholder" placeholder="Enter placeholder text" value="${data.placeholder || ""}" data-action="change->agent-inputs#updateConfig">
        </div>

        <div class="col-span-6 input-options-container" ${inputType === "select" ? "" : "style=\"display:none\""}>
          <label class="block text-xs font-medium text-foreground mb-1">Options</label>
          <textarea class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm font-mono input-options" rows="3" placeholder="value | Label\nvalue2 | Label 2" data-action="change->agent-inputs#updateConfig">${(data.options || []).map((opt) => typeof opt === "object" ? `${opt.value} | ${opt.label}` : opt).join("\n")}</textarea>
          <p class="text-[11px] text-muted-foreground mt-1">One per line. Format: <code>value | label</code> or just <code>label</code></p>
        </div>

        <div class="col-span-6 input-accept-container" ${inputType === "file" ? "" : "style=\"display:none\""}>
          <label class="block text-xs font-medium text-foreground mb-1">Accepted File Types</label>
          <input type="text" class="w-full px-2.5 py-1.5 border border-input rounded-md bg-background text-sm input-accept" placeholder=".pdf,.docx,.txt" value="${data.accept || ""}" data-action="change->agent-inputs#updateConfig">
        </div>
      </div>
    `

    this.containerTarget.appendChild(inputDiv)
    this.updateConfig()
    this.toggleEmptyState()
  }

  toggleInput(event) {
    event.preventDefault()
    const inputDiv = event.currentTarget.closest("[data-index]")
    const body = inputDiv?.querySelector(".input-body")
    const chevron = inputDiv?.querySelector(".input-chevron")
    if (!body || !chevron) return

    body.classList.toggle("hidden")
    chevron.classList.toggle("rotate-90", !body.classList.contains("hidden"))
  }

  syncInputMeta(event) {
    const inputDiv = event.target.closest("[data-index]")
    const heading = inputDiv?.querySelector(".input-heading")
    const nameInput = inputDiv?.querySelector(".input-name")
    if (heading) {
      const index = Number(inputDiv?.dataset.index || 0)
      heading.textContent = nameInput?.value || `Input #${index + 1}`
    }
    this.updateConfig()
  }

  removeInput(event) {
    event.preventDefault()
    event.target.closest("[data-index]")?.remove()
    this.renumberInputs()
    this.updateConfig()
    this.toggleEmptyState()
  }

  renumberInputs() {
    Array.from(this.containerTarget.children).forEach((child, index) => {
      child.dataset.index = index
      const badge = child.querySelector(".input-index-badge")
      if (badge) badge.textContent = `${index + 1}`
      const heading = child.querySelector(".input-heading")
      const nameInput = child.querySelector(".input-name")
      if (heading) heading.textContent = nameInput?.value || `Input #${index + 1}`
    })
  }

  handleTypeChange(event) {
    const inputDiv = event.target.closest("[data-index]")
    const type = event.target.value

    const placeholderContainer = inputDiv.querySelector(".input-placeholder-container")
    const optionsContainer = inputDiv.querySelector(".input-options-container")
    const acceptContainer = inputDiv.querySelector(".input-accept-container")
    const typeBadge = inputDiv.querySelector(".input-type-badge")

    if (type === "select") {
      placeholderContainer.style.display = "none"
      optionsContainer.style.display = "block"
      acceptContainer.style.display = "none"
    } else if (type === "file") {
      placeholderContainer.style.display = "none"
      optionsContainer.style.display = "none"
      acceptContainer.style.display = "block"
    } else {
      placeholderContainer.style.display = "block"
      optionsContainer.style.display = "none"
      acceptContainer.style.display = "none"
    }

    if (typeBadge) typeBadge.textContent = type
    this.updateConfig()
  }

  starterPromptsValue() {
    if (!this.hasStarterPromptsTarget) return []

    return this.starterPromptsTarget.value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const [title, ...rest] = line.split("|")
        return {
          title: title.trim(),
          prompt: rest.join("|").trim()
        }
      })
      .filter((prompt) => prompt.title.length > 0 && prompt.prompt.length > 0)
  }

  updateConfig() {
    let currentConfig = {}
    try {
      currentConfig = this.configFieldTarget.value ? JSON.parse(this.configFieldTarget.value) : {}
    } catch (_) {
      currentConfig = {}
    }

    const inputs = Array.from(this.containerTarget.children).map((inputDiv) => {
      const optionsText = inputDiv.querySelector(".input-options").value

      return {
        name: inputDiv.querySelector(".input-name").value,
        label: inputDiv.querySelector(".input-label").value,
        input_type: inputDiv.querySelector(".input-type").value,
        required: inputDiv.querySelector(".input-required").checked,
        primary: inputDiv.querySelector(".input-primary").checked,
        placeholder: inputDiv.querySelector(".input-placeholder").value,
        options: optionsText.split("\n").map((opt) => opt.trim()).filter((opt) => opt.length > 0).map((opt) => {
          if (opt.includes("|")) {
            const [value, ...rest] = opt.split("|")
            return { value: value.trim(), label: rest.join("|").trim() }
          }
          return { value: opt, label: opt }
        }),
        accept: inputDiv.querySelector(".input-accept").value
      }
    })

    const selectedTools = this.hasToolCheckboxTarget ? this.toolCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value) : []
    const documentSearchCheckbox = this.hasToolCheckboxTarget
      ? this.toolCheckboxTargets.find((cb) => cb.value === "document_search")
      : null

    const nextConfig = {
      ...currentConfig,
      coverage_summary: this.hasCoverageSummaryTarget ? this.coverageSummaryTarget.value.trim() : "",
      guide_url: this.hasGuideUrlTarget ? (this.guideUrlTarget.value.trim() || undefined) : undefined,
      starter_prompts: this.starterPromptsValue(),
      tools: selectedTools,
      inputs
    }

    if (documentSearchCheckbox) nextConfig.document_search_enabled = documentSearchCheckbox.checked

    if (this.hasHtmlFastModelTarget) nextConfig.fast_model = this.htmlFastModelTarget.value.trim()
    if (this.hasHtmlRouterModelTarget) nextConfig.router_model = this.htmlRouterModelTarget.value.trim() || undefined
    if (this.hasHtmlRouterJsonModeTarget) nextConfig.router_json_mode = this.htmlRouterJsonModeTarget.checked
    if (this.hasHtmlFullModelTarget) nextConfig.model = this.htmlFullModelTarget.value.trim()
    if (this.hasHtmlFastStaticByDefaultTarget) nextConfig.fast_static_by_default = this.htmlFastStaticByDefaultTarget.checked
    if (this.hasHtmlFastFixedStyleTarget) nextConfig.fast_fixed_style = this.htmlFastFixedStyleTarget.checked
    if (this.hasHtmlFullFixedStyleTarget) nextConfig.full_fixed_style = this.htmlFullFixedStyleTarget.checked
    if (this.hasHtmlFullEnableThinkingTarget) nextConfig.full_enable_thinking = this.htmlFullEnableThinkingTarget.checked
    if (this.hasHtmlFullThinkingBudgetTarget) {
      const budget = this.htmlFullThinkingBudgetTarget.value.trim()
      nextConfig.full_thinking_budget = budget.length > 0 ? Number(budget) : null
    }
    if (this.hasHtmlFullRetryWithoutThinkingTarget) nextConfig.full_retry_without_thinking = this.htmlFullRetryWithoutThinkingTarget.checked
    if (this.hasWebArtifactsEnabledTarget) nextConfig.web_artifacts_enabled = this.webArtifactsEnabledTarget.checked

    this.configFieldTarget.value = JSON.stringify(nextConfig)
  }

  toggleEmptyState() {
    if (!this.hasEmptyStateTarget) return
    this.emptyStateTarget.classList.toggle("hidden", this.containerTarget.children.length > 0)
  }
}
